/**
 * Authentication Service - Account-based with JWT Session Tokens
 * Handles multi-provider OAuth authentication with proper session management
 */

import { routesPath } from '../App';
import { saveAuthToCookies, getAuthFromCookies, clearAuthCookies } from '../utils/crossDomainAuth';

// URL-decode display fields (name + email) defensively. Some user records
// in DDB have these stored URL-encoded from a historical double-encode
// bug. Always decoding once on read — idempotent for raw values, fixes
// stale ones without needing data migration.
const safeDecodeField = (s) => {
  if (!s || typeof s !== 'string' || !s.includes('%')) return s;
  try { return decodeURIComponent(s); } catch { return s; }
};
const safeDecodeUser = (u) => {
  if (!u) return u;
  return { ...u, name: safeDecodeField(u.name), email: safeDecodeField(u.email) };
};

class AuthService {
  constructor() {
    this.currentUser = this.getStoredUser();
  }

  // Create or get user session - use account_id as user ID
  createUserSession(userInfo = {}) {
    const accountId = userInfo.account_id || userInfo.id || userInfo.userId;
    if (!accountId) {
      throw new Error('Account ID is required for user session');
    }
    
    const user = {
      id: accountId,
      account_id: accountId,
      email: userInfo.email,
      name: userInfo.name || 'User',
      provider: userInfo.provider || 'unknown',
      createdAt: new Date().toISOString()
    };

    this.currentUser = user;
    
    // Save to cookies ONLY
    console.log('🔑 Logged in - Account ID:', user.account_id, '-', user.email);
    saveAuthToCookies(userInfo.sessionToken, user);

    return user;
  }

  // Get stored user from cookies ONLY
  getStoredUser() {
    try {
      const { user } = getAuthFromCookies();
      return user;
    } catch (error) {
      console.error('Failed to get user from cookies:', error);
      return null;
    }
  }

  // Get current user
  getCurrentUser() {
    // If not in memory, try to load from storage (cookies or localStorage)
    if (!this.currentUser) {
      this.currentUser = this.getStoredUser();
    }
    // Defensive decode of name/email — some legacy records have these
    // URL-encoded ("Jianbo%20Cui", "boopage%40gmail.com") because of a
    // historical double-encode in the OAuth redirect path. Decoding here
    // means every consumer (Sidebar, Header, MobileSidebar, etc.) renders
    // cleanly without needing per-component fixes.
    return safeDecodeUser(this.currentUser);
  }

  // Check if user is authenticated
  isAuthenticated() {
    const user = this.getStoredUser();
    const session = this.getStoredSession();
    return !!user && !!session?.sessionToken;
  }

  // Get stored session from cookies ONLY
  getStoredSession() {
    try {
      const { session } = getAuthFromCookies();
      return session ? { sessionToken: session } : null;
    } catch (error) {
      console.error('Failed to get session from cookies:', error);
      return null;
    }
  }

  // Get session token for API calls
  getSessionToken() {
    const session = this.getStoredSession();
    return session?.sessionToken;
  }

  // Logout
  // returnTo: optional path (e.g. "/connect?deviceCode=xxx") to land
  // on after the cookie wipe — used by the CLI authorize page's
  // "switch user" link so the new session can pick up the in-flight
  // device code instead of dropping it.
  logout(returnTo) {
    this.currentUser = null;
    clearAuthCookies();

    // Cross-domain belt+suspenders: cookies on the CURRENT domain are
    // gone, but if the user previously had a session on the OTHER
    // public domain (zibby.app vs zibby.dev) those cookies are
    // unreachable from JS here (same-origin). Drop a hidden iframe to
    // the other domain's /logout-iframe so the same clearAuthCookies()
    // runs in that context and old sessions get wiped too. zibby.app
    // is the deprecated domain; zibby.dev is primary — running the
    // cross-domain wipe in BOTH directions keeps switching painless
    // until zibby.app cookies fully expire (365-day TTL).
    try {
      const here = window.location.hostname;
      const peer = here.includes('zibby.dev')
        ? here.replace('zibby.dev', 'zibby.app')
        : here.includes('zibby.app')
          ? here.replace('zibby.app', 'zibby.dev')
          : null;
      if (peer) {
        const f = document.createElement('iframe');
        f.style.display = 'none';
        f.src = `https://${peer}/logout-iframe`;
        document.body.appendChild(f);
        // Small delay so the iframe gets a chance to load + run its
        // logout script before we navigate away.
        setTimeout(() => { try { f.remove(); } catch { /* noop */ } }, 1500);
      }
    } catch { /* never let cross-domain logout block the local one */ }

    const currentHost = window.location.host;
    const rootHost = currentHost.replace('studio.', '').replace('studio-', '');
    const dest = returnTo || routesPath.connect;
    // Brief delay so the iframe has a moment to fire before navigation
    setTimeout(() => {
      window.location.href = `${window.location.protocol}//${rootHost}${dest}`;
    }, 300);
  }

  // Google Sign In - Real OAuth Flow
  async googleSignIn() {
    try {
      console.log('🔄 Starting Google OAuth flow...');
      
      const { API_BASE_URL } = require('./api');
      const authUrl = `${API_BASE_URL}/google/oauth-init/auth`;
      
      // Get session token to pass authenticated user context
      const sessionToken = this.getSessionToken();
      const headers = {};
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
        console.log('✅ Including auth token for existing user');
      }
      
      const response = await fetch(authUrl, { headers });
      const data = await response.json();
      
      if (!data.authUrl) {
        throw new Error('Failed to get Google OAuth URL');
      }

      console.log('🔄 Using REDIRECT STRATEGY for Google OAuth');
      
      // Store current page to return to after OAuth (don't overwrite if already set by pricing flow)
      // Don't store /connect as return URL - default to /projects instead
      if (!sessionStorage.getItem('oauth_return_url') && window.location.pathname !== '/connect') {
        sessionStorage.setItem('oauth_return_url', window.location.pathname);
      }
      sessionStorage.setItem('oauth_provider', 'google');
      
      console.log(`🔗 Redirecting to OAuth URL: ${data.authUrl}`);
      
      // Redirect to OAuth URL (works on all devices)
      window.location.href = data.authUrl;
      return; // No promise needed for redirect

    } catch (error) {
      console.error('Google OAuth error:', error);
      throw error;
    }
  }

  // Microsoft Sign In - Real OAuth Flow  
  async microsoftSignIn() {
    try {
      const { API_BASE_URL } = require('./api');
      const authUrl = `${API_BASE_URL}/microsoft/oauth-init/auth`;
      
      // Get session token to pass authenticated user context
      const sessionToken = this.getSessionToken();
      const headers = {};
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
        console.log('✅ Including auth token for existing user');
      }
      
      const response = await fetch(authUrl, { headers });
      const data = await response.json();
      
      if (!data.authUrl) {
        throw new Error('Failed to get Microsoft OAuth URL');
      }

      console.log('🔄 Using REDIRECT STRATEGY for Microsoft OAuth');
      
      // Store current page to return to after OAuth (don't overwrite if already set by pricing flow)
      // Don't store /connect as return URL - default to /projects instead
      if (!sessionStorage.getItem('oauth_return_url') && window.location.pathname !== '/connect') {
        sessionStorage.setItem('oauth_return_url', window.location.pathname);
      }
      sessionStorage.setItem('oauth_provider', 'microsoft');
      
      console.log(`🔗 Redirecting to Microsoft OAuth URL: ${data.authUrl}`);
      
      // Redirect to OAuth URL (works on all devices)
      window.location.href = data.authUrl;
      return; // No promise needed for redirect

    } catch (error) {
      console.error('Microsoft OAuth error:', error);
      throw error;
    }
  }

  async requestPasscode(email) {
    const api = require('./api').default;
    const data = await api.post('/auth/passcode/request', { email: email.trim().toLowerCase() });
    return data;
  }

  async verifyPasscode(email, passcode) {
    const api = require('./api').default;
    const data = await api.post('/auth/passcode/verify', {
      email: email.trim().toLowerCase(),
      passcode: passcode.trim()
    });
    this.createUserSession({
      account_id: data.user.userId,
      id: data.user.userId,
      userId: data.user.userId,
      email: data.user.email,
      name: data.user.name,
      provider: data.user.provider || 'email',
      sessionToken: data.token
    });
    return data;
  }

  // Facebook Sign In - Real OAuth Flow  
  async facebookSignIn() {
    try {
      const { API_BASE_URL } = require('./api');
      const response = await fetch(`${API_BASE_URL}/facebook/oauth-init/auth`);
      const data = await response.json();
      
      if (!data.authUrl) {
        throw new Error('Failed to get Facebook OAuth URL');
      }

      // Always use redirect strategy to avoid popup blocking issues
      console.log('🔄 Using REDIRECT STRATEGY for Facebook OAuth');
      
      // Store current page to return to after OAuth (don't overwrite if already set by pricing flow)
      // Don't store /connect as return URL - default to /projects instead
      if (!sessionStorage.getItem('oauth_return_url') && window.location.pathname !== '/connect') {
        sessionStorage.setItem('oauth_return_url', window.location.pathname);
      }
      sessionStorage.setItem('oauth_provider', 'facebook');
      
      console.log(`🔗 Redirecting to Facebook OAuth URL: ${data.authUrl}`);
      
      // Redirect to OAuth URL (works on all devices)
      window.location.href = data.authUrl;
      return; // No promise needed for redirect

    } catch (error) {
      console.error('Facebook OAuth error:', error);
      throw error;
    }
  }
}

const authService = new AuthService();
export default authService;